import { MonitorSmartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

interface CreateShortcutButtonProps {
  /** "full" shows icon + label (header on larger screens). "icon" is icon-only (tight spaces). */
  variant?: "full" | "icon";
}

export function CreateShortcutButton({ variant = "full" }: CreateShortcutButtonProps) {
  const { canPrompt, isInstalled, isIOS, promptInstall } = useInstallPrompt();

  if (isInstalled) return null;

  const handleClick = async () => {
    if (isIOS) {
      toast.info("Add FreeDailyPro to your Home Screen", {
        description: "Tap the Share icon in Safari, then choose \"Add to Home Screen.\"",
        duration: 9000,
      });
      return;
    }

    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === "accepted") {
        toast.success("Shortcut created! Look for the FreeDailyPro icon.");
      }
      return;
    }

    // Chrome only offers the real, full install (a genuine installed app
    // with its own permissions, separate from a browser tab) after its
    // own engagement heuristics decide to -- something this page can't
    // control or force early. Hiding the button entirely until then
    // used to mean the only path visible was the browser's own generic
    // "Add to Home Screen" menu item, which creates a bare bookmark
    // shortcut instead -- no separate camera/microphone permissions of
    // its own, and no way to reach the regular per-site permission
    // controls from inside it, which is a real dead end this exact
    // message is meant to prevent someone from wandering into blind.
    toast.info("A full install isn't available yet in this browser session", {
      description: "Browse the site a bit more, then try this button again. Using your browser's own menu " +
        "to \"Add to Home Screen\" now instead will create a basic shortcut, not a full installed app — camera " +
        "and microphone permissions for it are shared with your regular browser tabs rather than managed " +
        "separately, so if a tool needs the camera, grant it in a normal tab first.",
      duration: 10000,
    });
  };

  if (variant === "icon") {
    return (
      <Button variant="ghost" size="icon" onClick={handleClick} title="Create shortcut for quick access" aria-label="Create shortcut for quick access">
        <MonitorSmartphone className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Button variant="outline" size="sm" onClick={handleClick} className="gap-1.5 hidden md:inline-flex">
      <MonitorSmartphone className="h-3.5 w-3.5" />
      <span className="text-xs">Create Shortcut</span>
    </Button>
  );
}
